import {defineStore} from 'pinia';
import { AxiosRequestConfig } from "axios";
import handleTeacherError from "@/util/handle_teacher_error";
import apiRequest from '@/util/api-request';

interface Conditions {
  defaultAcademicSessionId: number;
  academicSessionIds: { value: number; name: string; }[];
  academicYears: { [academicSessionId: number]: { value: number; name: string; }[]; };
  classrooms: {
    [academicSessionId: number]: {
      [academicYear: number]: { value: string; name: string; }[];
    };
  };
  learningStatusClassBaseUrl: string;
}

interface ChartData {
  labels: string[];
  datasets: { label: string; data: number[]; extraData?: number[]; }[];
}

interface LearningStatusYear {
  conditions: Conditions;
  progress_rate_chart_data: ChartData;
  studied_questions_chart_data: ChartData;
  studied_time_chart_data: ChartData;
}

const subjectIdMap: { [value: string] : number } = {
  // FIXME: ゆくゆくは教科IDもサーバー側から取得した方が良いかも
  //  もしくは、この変換表はサーバー側に持ったほうがよいのかな...
  //  => FIXME: ひとまず consts に引っ越す, 引き当ては呼び出し元 component のほうでやる
  "all": 0,
  "kokugo": 1,
  "shakai": 2,
  "sugaku": 3,
  "rika": 4,
  "eigo": 5,
}

const useLearningStatusYearStore = defineStore('learningStatusYearStore', {
  // state
  state: (): LearningStatusYear => {
    return {
      conditions: null,
      progress_rate_chart_data: null,
      studied_questions_chart_data: null,
      studied_time_chart_data: null,
    }
  },
  // getters
  getters: {
    progressRateChartData: (state) => {
      return state.progress_rate_chart_data
    },
    yearConditions: (state) => {
      return state.conditions
    },
    studiedQuestionsChartData: (state) => {
      return state.studied_questions_chart_data
    },
    studiedTimeChartData: (state) => {
      return state.studied_time_chart_data
    }
  },
  // actions
  actions: {
    async fetchLearningStatusYearConditions() {
      try {
        const config: AxiosRequestConfig = {
          method: "get",
          url: "/teacher/api/learning_status/year/conditions/",
          headers: {
            "Content-Type": "application/json",
          },
        };
        const conditions = await apiRequest(config);
        this.conditions = conditions.data
      } catch (e) {
        handleTeacherError(e)
      }
    },
    async fetchLearningStatusYearProgressRate({academic_session_id, academic_year, subject, period, work}) {
      try {
        const config: AxiosRequestConfig = {
          method: "get",
          url: "/teacher/api/learning_status/year/progress_rate/",
          headers: {
            "Content-Type": "application/json",
          },
          params: {
            academic_session_id: academic_session_id,
            academic_year: academic_year,
            subject: subjectIdMap[subject],
            period: period,
            ...(work && { work_id: work }),
          },
        };
        const chartData = await apiRequest(config);
        if (chartData.data && Object.keys(chartData.data).length > 0) {
          this.progress_rate_chart_data = chartData.data
        }
      } catch (e) {
        this.progress_rate_chart_data = null
        handleTeacherError(e)
      }
    },
    async fetchLearningStatusYearStudiedQuestions({academic_session_id, academic_year, subject, period, work}) {
      try {
        const config: AxiosRequestConfig = {
          method: "get",
          url: "/teacher/api/learning_status/year/studied_questions/",
          headers: {
            "Content-Type": "application/json",
          },
          params: {
            academic_session_id: academic_session_id,
            academic_year: academic_year,
            subject: subjectIdMap[subject],
            period: period,
            ...(work && { work_id: work }),
          },
        };
        const chartData = await apiRequest(config);
        if (chartData.data && Object.keys(chartData.data).length > 0) {
          this.studied_questions_chart_data = chartData.data
        }
      } catch (e) {
        this.studied_questions_chart_data = null
        handleTeacherError(e)
      }
    },
    async fetchLearningStatusYearStudiedTime({academic_session_id, academic_year, subject, period, work}) {
      try {
        const config: AxiosRequestConfig = {
          method: "get",
          url: "/teacher/api/learning_status/year/studied_time/",
          headers: {
            "Content-Type": "application/json",
          },
          params: {
            academic_session_id: academic_session_id,
            academic_year: academic_year,
            subject: subjectIdMap[subject],
            period: period,
            ...(work && { work_id: work }),
          }
        };
        const chartData = await apiRequest(config);
        if (chartData.data && Object.keys(chartData.data).length > 0) {
          this.studied_time_chart_data = chartData.data
        }
      } catch (e) {
        this.studied_time_chart_data = null
        handleTeacherError(e)
      }
    }
  }
})

export {useLearningStatusYearStore, Conditions, ChartData}